import { logger } from '@/utils/logger';
import { apiClient } from '@/lib/api-client';
import { AnalysisResults, FlowStatistics, CsvSiteDataRow } from '@/components/shared/types';
import { validateVideoResolution, type VideoValidationResult } from '@/utils/videoValidation';


const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isValidUUID = (id: string): boolean => {
  return UUID_REGEX.test(id);
};

export interface Video {
  uid: string;
  video_title: string;
  video_url: string;
  processed_video_url?: string;
  thumbnail_url?: string;
  flow_json_url?: string;
  created_at: string;
  updated_at?: string;
  status?: 'processing' | 'ready' | 'failed' | 'canceled';

  // Video metadata fields
  width?: number;
  height?: number;
  fps?: number;
  duration_seconds?: number;
  file_size_mb?: number;
  codec?: string;
  resolution_class?: string;
  processing_strategy?: string;
  total_pixels?: number;
  aspect_ratio?: string;
}

export interface VideoAnalysis {
  id: string;
  videoId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'canceled';
  progress: number;
  results: AnalysisResults | null;
  createdAt: string;
  updatedAt: string;
}

export interface ROIPoint {
  x: number;
  y: number;
}

export interface ROIPolygon {
  points: ROIPoint[];
  label?: string;
}

export interface ReferenceLine {
  start: ROIPoint;
  end: ROIPoint;
  realWorldLength: number;
  unit: string;
}

export interface FlowAnalysisRequest {
  technique?: string;
  resolution?: string;
  frameRate?: number;
}

export interface FlowAnalysis {
  uid: string;
  meters_per_pixel?: number;
  flow_statistics?: FlowStatistics;
  processed_video_url?: string;
  flow_json_url?: string;
  roi_data?: Record<string, unknown>;
  reference_measurements?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface ReportModel {
  id: string;  // S3 object key - not a DB uid
  filename: string;
  format: string;
  type: 'video_analysis' | 'simulation';
  video_uid?: string;
  video_title?: string;
  simulation_uid?: string;
  simulation_name?: string;
  size: number;
  created_at: string;
  download_url?: string;
}

export interface ReportsResponse {
  reports: ReportModel[];
  total_count: number;
  user_uid: string;
}


const toPermanentUrl = (
  original: string | undefined,
  videoId: string,
  kind: 'thumbnail' | 'processed' | 'flow'
): string | undefined => {
  if (!original) return undefined;
  const route = kind === 'thumbnail' ? 'thumbnail' : kind === 'processed' ? 'processed' : 'flow';
  return `/api/v1/r/${route}/${videoId}`;
};

const normalizeVideoUrls = <T extends { uid: string; thumbnail_url?: string; processed_video_url?: string; flow_json_url?: string }>(
  v: T
): T => ({
  ...v,
  thumbnail_url: toPermanentUrl(v.thumbnail_url, v.uid, 'thumbnail'),
  processed_video_url: toPermanentUrl(v.processed_video_url, v.uid, 'processed'),
  flow_json_url: toPermanentUrl(v.flow_json_url, v.uid, 'flow')
});

export const videosApi = {
  // In-flight coalescing map for flow JSON to prevent duplicate parallel fetches
  _flowJsonInFlight: {} as Record<string, Promise<unknown>>,
  _flowJsonResolved: {} as Record<string, unknown>,

  getVideos: (() => {
    let lastPromise: Promise<Video[]> | null = null;
    let lastFetchTime = 0;
    const minInterval = 500;

    return async () => {
      const now = Date.now();

      if (lastPromise && now - lastFetchTime < minInterval) {
        return lastPromise;
      }

      lastFetchTime = now;
      lastPromise = apiClient.get('/v1/videos').then((videos: unknown) => {
        if (Array.isArray(videos)) {
          return videos.map((v) => normalizeVideoUrls(v as Video)) as Video[];
        }
        return [] as Video[];
      });
      return lastPromise;
    };
  })(),

  getVideo: async (videoId: string) => {
    if (!isValidUUID(videoId)) {
      logger.error(`Invalid video ID format: ${videoId}`);
      return Promise.reject(new Error(`Invalid video ID format: ${videoId}`));
    }
    const response = await apiClient.get(`/v1/videos/${videoId}`);
    return normalizeVideoUrls(response);
  },

  uploadVideo: async (file: File, onProgress?: (percentage: number) => void) => {
    const validation = await validateVideoResolution(file);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Video validation failed');
    }
    return apiClient.uploadFile('/v1/videos', file, onProgress);
  },

  deleteVideo: (videoId: string) =>
    apiClient.delete(`/v1/videos/${videoId}`),

  startAnalysis: (
    videoId: string,
    roiPolygon: ROIPolygon,
    referenceLine: ReferenceLine | null,
    params: FlowAnalysisRequest = {},
    channelDepth?: number,
    currentPowerProduction?: number,
    siteName?: string,
    coordinates?: string,
    latitude?: number,
    longitude?: number,
    headHeight?: number,
    yearBuilt?: number,
    numberOfGates?: number,
    numberOfSections?: number,
    csvSiteData?: CsvSiteDataRow[] | null
  ) => {
    if (!isValidUUID(videoId)) {
      logger.error(`Invalid video ID format: ${videoId}`);
      return Promise.reject(new Error(`Invalid video ID format: ${videoId}`));
    }

    const payload: Record<string, unknown> = {
      video_uid: videoId,
      roi_polygon: {
        points: roiPolygon.points,
        label: roiPolygon.label
      },
      analysis_config: {
        technique: params.technique,
        resolution: params.resolution,
        frame_rate: params.frameRate
      }
    };

    // Only send reference_line for manual calibration; AprilTag mode relies on
    // a previously-stored meters_per_pixel on the video record.
    if (referenceLine) {
      payload.reference_line = {
        start_point: referenceLine.start,
        end_point: referenceLine.end,
        real_distance: referenceLine.realWorldLength,
        label: referenceLine.unit
      };
    }

    if (typeof channelDepth === 'number' && Number.isFinite(channelDepth) && channelDepth >= 0) {
      payload.channelDepth = channelDepth;
    }
    if (
      typeof currentPowerProduction === 'number' &&
      Number.isFinite(currentPowerProduction) &&
      currentPowerProduction >= 0
    ) {
      payload.currentPowerProduction = currentPowerProduction;
    }

    // Site information parameters
    if (siteName && siteName.trim().length > 0) {
      payload.siteName = siteName.trim();
    }
    if (coordinates && coordinates.trim().length > 0) {
      payload.coordinates = coordinates.trim();
    }
    if (typeof latitude === 'number' && Number.isFinite(latitude) && latitude >= -90 && latitude <= 90) {
      payload.latitude = latitude;
    }
    if (typeof longitude === 'number' && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180) {
      payload.longitude = longitude;
    }
    if (typeof headHeight === 'number' && Number.isFinite(headHeight) && headHeight > 0) {
      payload.headHeight = headHeight;
    }
    if (typeof yearBuilt === 'number' && Number.isFinite(yearBuilt) && yearBuilt > 0) {
      payload.yearBuilt = yearBuilt;
    }
    if (typeof numberOfGates === 'number' && Number.isFinite(numberOfGates) && numberOfGates > 0) {
      payload.numberOfGates = numberOfGates;
    }
    if (typeof numberOfSections === 'number' && Number.isFinite(numberOfSections) && numberOfSections > 0) {
      payload.numberOfSections = numberOfSections;
    }

    // Add CSV site data if provided
    if (csvSiteData && Array.isArray(csvSiteData) && csvSiteData.length > 0) {
      payload.csvSiteData = csvSiteData[0];
    }

    return apiClient.post(`/v1/videos/${videoId}/analysis`, payload);
  },

  getAnalysis: async (videoId: string) => {
    const response = await apiClient.get(`/v1/videos/${videoId}/analysis`);
    return response;
  },

  updateROI: (videoId: string, polygon: ROIPolygon) =>
    apiClient.post(`/v1/videos/${videoId}/roi`, polygon),

  updateReference: (videoId: string, reference: ReferenceLine) =>
    apiClient.post(`/v1/videos/${videoId}/reference`, {
      start_point: reference.start,
      end_point: reference.end,
      real_distance: reference.realWorldLength,
      label: reference.unit
    }),

  getReference: (videoId: string) =>
    apiClient.get(`/v1/videos/${videoId}/reference`),

  getProcessingStatus: async (videoId: string) => {
    if (!isValidUUID(videoId)) {
      logger.error(`Invalid video ID format in getProcessingStatus: ${videoId}`);
      return Promise.reject(new Error(`Invalid video ID format: ${videoId}`));
    }
    const response = await apiClient.get(`/v1/videos/${videoId}/processing-status`);
    return response;
  },

  getThumbnail: async (videoId: string) => {
    if (!isValidUUID(videoId)) {
      logger.error(`Invalid video ID format in getThumbnail: ${videoId}`);
      return Promise.reject(new Error(`Invalid video ID format: ${videoId}`));
    }
    return `/api/v1/r/thumbnail/${videoId}`;
  },

  getProcessedVideo: async (videoId: string) => {
    if (!isValidUUID(videoId)) {
      logger.error(`Invalid video ID format in getProcessedVideo: ${videoId}`);
      return Promise.reject(new Error(`Invalid video ID format: ${videoId}`));
    }
    return `/api/v1/r/processed/${videoId}`;
  },

  // The /r/processed redirect re-signs on every request, so a video element's range
  // requests each pay a backend round trip. Resolving the storage URL once lets the
  // browser stream and seek against it directly.
  getProcessedVideoDirectUrl: async (videoId: string): Promise<string> => {
    if (!isValidUUID(videoId)) {
      logger.error(`Invalid video ID format in getProcessedVideoDirectUrl: ${videoId}`);
      return Promise.reject(new Error(`Invalid video ID format: ${videoId}`));
    }
    try {
      const response = await apiClient.get(`/v1/videos/${videoId}/processed-url`);
      const url = (response as { url?: string })?.url;
      if (url) return url;
    } catch (error) {
      logger.error(`Failed to resolve direct processed video URL for ${videoId}:`, error);
    }
    return `/api/v1/r/processed/${videoId}`;
  },

  getAnalysisReport: (videoId: string, format: string = 'pdf', simulationData: Record<string, unknown> | null = null) => {
    const params = new URLSearchParams();
    params.append('format', format);

    if (simulationData) {
      params.append('include_simulation', 'true');

      return apiClient.post(
        `/v1/videos/${videoId}/analysis/report?${params.toString()}`,
        { simulation_data: simulationData }
      );
    }
    return apiClient.get(`/v1/videos/${videoId}/analysis/report?${params.toString()}`);
  },

  cancelProcessing: (videoId: string) => {
    if (!isValidUUID(videoId)) {
      logger.error(`Invalid video ID format: ${videoId}`);
      return Promise.reject(new Error(`Invalid video ID format: ${videoId}`));
    }
    return apiClient.post(`/v1/videos/${videoId}/cancel`);
  },

  getFlowJson: async (videoId: string) => {
    if (!isValidUUID(videoId)) {
      logger.error(`Invalid video ID format in getFlowJson: ${videoId}`);
      return Promise.reject(new Error(`Invalid video ID format: ${videoId}`));
    }
    if (videosApi._flowJsonResolved[videoId]) {
      return videosApi._flowJsonResolved[videoId];
    }
    const existing = videosApi._flowJsonInFlight[videoId];
    if (existing) {
      return await existing;
    }

    const promise = (async () => {
      try {
        const data = await apiClient.get(`/v1/r/flow/${videoId}`, {}, { suppressToast: true });
        const redirectCheck = data as { _redirect_failed?: boolean } | undefined;
        if (redirectCheck && redirectCheck._redirect_failed) {
          throw new Error('__FLOW_REDIRECT_FAILED__');
        }
        videosApi._flowJsonResolved[videoId] = data;
        return data;
      } catch (primaryErr) {
        const response = await apiClient.get(`/v1/videos/${videoId}/flow-json`, {}, { suppressToast: true, treat404AsNull: true });

        if (!response) {
          return null;
        }

        if (response && response.flow_json_url) {
          const flowDataResponse = await fetch(response.flow_json_url);
          if (!flowDataResponse.ok) {
            throw new Error(`Failed to fetch flow data: ${flowDataResponse.status}`);
          }
          const data = await flowDataResponse.json();
          videosApi._flowJsonResolved[videoId] = data;
          return data;
        } else {
          return null;
        }
      }
    })();

    videosApi._flowJsonInFlight[videoId] = promise;
    try {
      const result = await promise;
      return result;
    } finally {
      delete videosApi._flowJsonInFlight[videoId];
    }
  },

  getReports: async (): Promise<ReportsResponse> => {
    const response = await apiClient.get('/v1/reports');
    return response;
  },

  deleteReport: async (reportId: string): Promise<void> => {
    await apiClient.delete(`/v1/reports/${encodeURIComponent(reportId)}`);
  },

  getFlowAnalyses: async (videoId: string): Promise<FlowAnalysis[]> => {
    if (!isValidUUID(videoId)) {
      logger.error(`Invalid video ID format in getFlowAnalyses: ${videoId}`);
      return Promise.reject(new Error(`Invalid video ID format: ${videoId}`));
    }
    try {
      const response = await apiClient.get(`/v1/videos/${videoId}/flow-analyses`, {}, { suppressToast: true, treat404AsNull: true });
      return response || [];
    } catch (error) {
      logger.warn(`Could not fetch flow analyses for video ${videoId}:`, error);
      return [];
    }
  }
};
